import { useCallback } from 'react';

/**
 * Custom hook to handle form validation errors and tab switching
 * @param {string} tabsForm - Current active tab
 * @param {function} setTabsForm - Function to set active tab
 * @returns {function} handleFormErrors - Function to handle form errors
 */
export const useFormErrorHandler = (tabsForm, setTabsForm) => {
  const descriptionFields = [
    'tournamentName',
    'tournamentDescription',
    'organizer',
    'location',
    'facebookLink',
    'instagramLink',
    'discordLink',
    'twitterLink',
    'prizeFund',
    'rules',
    'layer',
    'type',
    'platform',
    'community'
  ];

  const settingsFields = [
    'numOfStages',
    'numOfParticipantsInGroup',
    'numOfParticipantsToNextStage',
    'numOfPointsForKills',
    'maxNumber',
    'maxNumInGroup',
    'dateStartTime',
    'dateFinishTime',
    'format',
    'games',
    'registrationStartTime',
    'registrationFinishTime',
    'matchStart',
    'matchesFormat',
    'intervalBetweenRounds',
    'matchesInInterval',
    'substitutePlayer',
    'tiebreak',
    'directSlots',
    'secondChance',
    'secondChanceSlots',
    'system',
    'elegibilityRules',
    'gender',
    'canJudge',
    'matchInterval',
  ];

  /**
   * Handles form validation errors by switching to appropriate tab and scrolling to error field
   * @param {object} errors - Form validation errors object from react-hook-form
   * @returns {boolean} - Returns true if errors were handled, false if no errors
   */
  const handleFormErrors = useCallback((errors) => {
    const errorFields = Object.keys(errors);
    if (errorFields.length === 0) return false;

    const firstErrorField = errorFields[0];
    
    let targetTab = null;
    if (descriptionFields.includes(firstErrorField)) {
      targetTab = 'description';
    } else if (settingsFields.includes(firstErrorField)) {
      targetTab = 'settings';
    }

    const isNeedSwitchTab = targetTab && tabsForm !== targetTab;
    if (isNeedSwitchTab) {
      setTabsForm(targetTab);
    }

    // Scroll to the error field - use longer timeout if we switched tabs to wait for DOM update
    const timeout = isNeedSwitchTab ? 400 : 50;

    setTimeout(() => {
      let errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      
      // If the element is a hidden input (type="hidden") or has display: none,
      // try to find the sibling control element for react-select components
      if (errorElement && (errorElement.type === 'hidden' || window.getComputedStyle(errorElement).display === 'none')) {
        // Look for sibling react-select wrapper (div with id starting with "react-select-")
        let visibleControl = null;
        const nextSibling = errorElement.nextElementSibling;
        
        if (nextSibling) {
          // Find the span with id starting with 'react-select-' inside the react-select wrapper
          visibleControl = nextSibling.querySelector('span[id^="react-select-"]');
        }
        
        // Fallback: look for control in parent
        if (!visibleControl) {
          visibleControl = errorElement.parentElement?.querySelector('.custom_select__control');
        }
        
        if (visibleControl) {
          errorElement = visibleControl;
        }
      }
      
      if (!errorElement) {
        console.warn(`Could not find error element: ${firstErrorField}`);
        return;
      }

      errorElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, timeout);

    return true;
  }, [tabsForm, setTabsForm]);

  return handleFormErrors;
};
